import {queryOptions, useMutation, useQuery, useQueryClient} from '@tanstack/react-query'
import {AgencyKitRepository} from '../repositories/AgencyKitRepository.ts'
import {AuthenticationRepository} from '../repositories/AuthenticationRepository.ts'
import {AgencyKit, CreateAgencyKitPayload} from '../domain/entities/AgencyKit.ts'
import {isKitStalled} from '../components/agencyKits/kitDisplayStatus.ts'

const agencyKitRepository = new AgencyKitRepository(new AuthenticationRepository())

export const AGENCY_KITS_QUERY_KEY = 'agency-kits' as const
const AGENCY_KIT_QUERY_KEY = 'agency-kit' as const
const AGENCY_KIT_SALES_REPS_QUERY_KEY = 'agency-kit-sales-reps' as const

// Generation runs in a Cloud Task (deck personalization + Gotenberg PDF), so a
// PENDING kit is polled until it settles (or stalls, see kitDisplayStatus).
const PENDING_POLL_MS = 4000
export const isKitGenerating = (kit: AgencyKit) => kit.status === 'PENDING' && !isKitStalled(kit)

// Shared by the kit detail and the dashboard-wide "kit ready" notifier, so
// both read (and poll) the same cache entry.
export const agencyKitQueryOptions = (id: string | null) => queryOptions({
    queryKey: [AGENCY_KIT_QUERY_KEY, id],
    queryFn: () => agencyKitRepository.getAgencyKit(id!),
    enabled: !!id,
    refetchInterval: (q) => q.state.data && isKitGenerating(q.state.data) ? PENDING_POLL_MS : false,
})

export function useAgencyKits(agencyId?: string) {
    const query = useQuery({
        queryKey: [AGENCY_KITS_QUERY_KEY, agencyId ?? null],
        queryFn: () => agencyKitRepository.getAgencyKits(agencyId),
        refetchInterval: (q) => q.state.data?.kits.some(isKitGenerating) ? PENDING_POLL_MS : false,
    })

    return {
        kits: query.data?.kits ?? [],
        isLoadingKits: query.isLoading,
        kitsError: query.error,
    }
}

export function useAgencyKit(id: string | null) {
    const query = useQuery(agencyKitQueryOptions(id))

    return {
        kit: query.data,
        isLoadingKit: query.isLoading,
        kitError: query.error,
    }
}

export function useAgencyKitSalesReps() {
    const query = useQuery({
        queryKey: [AGENCY_KIT_SALES_REPS_QUERY_KEY],
        queryFn: () => agencyKitRepository.getAgencyKitSalesReps(),
        staleTime: Infinity,
    })

    return {
        salesReps: query.data?.salesReps ?? [],
        isLoadingSalesReps: query.isLoading,
    }
}

export const fetchAgencyKitLogo = (id: string) => agencyKitRepository.getAgencyKitLogo(id)

export function useAgencyKitMutations() {
    const queryClient = useQueryClient()
    const invalidate = () => {
        queryClient.invalidateQueries({queryKey: [AGENCY_KITS_QUERY_KEY]})
        queryClient.invalidateQueries({queryKey: [AGENCY_KIT_QUERY_KEY]})
    }

    const createMutation = useMutation({
        mutationFn: (payload: CreateAgencyKitPayload) => agencyKitRepository.createAgencyKit(payload),
        onSuccess: invalidate,
    })

    const setActiveMutation = useMutation({
        mutationFn: ({id, isActive}: { id: string, isActive: boolean }) => agencyKitRepository.setAgencyKitActive(id, isActive),
        onSuccess: invalidate,
    })

    const retryMutation = useMutation({
        mutationFn: (id: string) => agencyKitRepository.retryAgencyKit(id),
        onSuccess: invalidate,
    })

    return {
        createKit: createMutation.mutateAsync,
        retryKit: retryMutation.mutateAsync,
        isRetryingKit: retryMutation.isPending,
        setKitActive: setActiveMutation.mutateAsync,
        isSettingKitActive: setActiveMutation.isPending,
    }
}
